import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { VENDOR_MEMBER_REFUSED_REASON } from '../shared/constants/realtime.constants';

/** Payload from Pusher `lane-attention` on `location-ID-{uuid}`. */
export interface LaneAttentionAlert {
  id: string;
  reason: string;
  title: string;
  message: string;
  source_event?: string | null;
  location_id?: string | null;
  plate?: string | null;
  state?: string | null;
  branch?: string | null;
  lane_id?: string | null;
  lane_name?: string | null;
  lpr_entry_id?: string | null;
  /**
   * CWS-1679: the chooser payload (purchase_options, lane, entry) for a
   * `multi_eligible_selection` card, so a tablet on another lane can open it from the card.
   */
  payload?: Record<string, any> | null;
  timestamp: Date;
  expiresAt: number;
}

export const MULTI_ELIGIBLE_SELECTION_REASON = 'multi_eligible_selection';

/**
 * CWS-1679: "<lane>: multiple memberships, select one" for a multi-eligible chooser, so a
 * tablet on another lane knows which lane is waiting. Null when the lane name is unknown.
 */
export function multiEligibleLaneTitle(
  data: Record<string, any> | null | undefined
): string | null {
  const laneName = String(data?.['lane_name'] ?? '').trim();
  if (!laneName) {
    return null;
  }
  const what =
    String(data?.['selection_kind'] ?? '').trim() === 'packages'
      ? 'multiple packages'
      : 'multiple memberships';
  return `${laneName}: ${what}, select one`;
}

const AUTO_DISMISS_MS = 5 * 60 * 1000;
const MAX_ALERTS = 12;

/**
 * TEMPORARY (CWS-XXX): master kill-switch that fully disables ALL lane-attention toasts in the
 * UI (every reason, including the "Vehicle not found" title override). Frontend-only — the
 * backend still publishes/logs the `lane-attention` event; this only stops the UI from
 * displaying/stacking any toast.
 *
 * This is expected to be reverted the next day. TO RESTORE: flip this to `false`. Granular
 * per-reason behavior below (SUPPRESSED_LANE_ATTENTION_REASONS) is intentionally kept intact so
 * that flipping this switch back to `false` immediately returns to the previous behavior.
 */
const SUPPRESS_ALL_LANE_ATTENTION_TOASTS = true;

/**
 * Reasons that still show a toast even when {@link SUPPRESS_ALL_LANE_ATTENTION_TOASTS} is on.
 * CWS-889: attendant must see multi-package / multi-membership selection prompts.
 * CWS-1650: `vendor_member_refused` tells attendants on the other lanes that a vendor
 * member was refused (NotificationService only forwards it to them).
 * `membership_limit_reached` is not listed: that flow already opens the order-request
 * modal via `create-order-request`.
 */
const LANE_ATTENTION_TOAST_ALLOWLIST: ReadonlySet<string> = new Set([
  'multi_eligible_selection',
  VENDOR_MEMBER_REFUSED_REASON,
]);

/**
 * TEMPORARY (CWS-XXX): lane-attention reasons whose toast is suppressed in the UI while
 * they are being debugged. Frontend-only — the backend still publishes the `lane-attention`
 * event; this only prevents the toast from displaying/stacking. Remove entries (or clear the
 * set) to restore display.
 *  - `no_order_or_membership`   → "No active order or membership"
 *  - `create_customer_required` → "Customer not found"
 */
const SUPPRESSED_LANE_ATTENTION_REASONS: ReadonlySet<string> = new Set([
  'no_order_or_membership',
  'create_customer_required',
]);

/**
 * Stackable 5-minute staff attention alerts (lane membership / prepaid / no-order).
 * Separate from modal NotificationService flow.
 */
@Injectable({
  providedIn: 'root',
})
export class LaneAttentionService {
  private readonly alertsSubject = new BehaviorSubject<LaneAttentionAlert[]>([]);
  readonly alerts$ = this.alertsSubject.asObservable();

  private dismissTimers = new Map<string, ReturnType<typeof setTimeout>>();

  pushFromPusher(data: Record<string, any> | null | undefined): void {
    // TEMPORARY (CWS-XXX): fully disable lane-attention toasts except allowlisted reasons
    // (CWS-889 multi-eligible). Backend still publishes/logs the event.
    // TO RESTORE: set SUPPRESS_ALL_LANE_ATTENTION_TOASTS = false (reverts to per-reason behavior).
    const reasonPreview = String(data?.['reason'] ?? '').trim();
    if (SUPPRESS_ALL_LANE_ATTENTION_TOASTS && !LANE_ATTENTION_TOAST_ALLOWLIST.has(reasonPreview)) {
      return;
    }

    if (!data || typeof data !== 'object') {
      return;
    }

    const reason = String(data['reason'] ?? 'any').trim() || 'any';
    const isMultiEligible = reason === MULTI_ELIGIBLE_SELECTION_REASON;
    const title =
      (isMultiEligible ? multiEligibleLaneTitle(data) : null) ||
      String(data['title'] ?? 'Lane attention required').trim() ||
      'Lane attention required';
    const message =
      String(data['message'] ?? 'Staff action needed — gate cannot open automatically.').trim() ||
      'Staff action needed — gate cannot open automatically.';

    // TEMPORARY (CWS-XXX): suppress specific lane-attention toasts in the UI while they are
    // being debugged (see SUPPRESSED_LANE_ATTENTION_REASONS). Frontend-only; the backend still
    // publishes the event. All other reasons are unaffected. Revert to restore display.
    if (SUPPRESSED_LANE_ATTENTION_REASONS.has(reason)) {
      return;
    }

    const lprEntryId = String(data['lpr_entry_id'] ?? '').trim();
    const plate = String(data['plate'] ?? '').trim();
    const locationId = String(data['location_id'] ?? '').trim();

    // Dedupe same LPR row / plate+reason within the live stack
    const existing = this.alertsSubject.value;
    const dedupeKey = lprEntryId
      ? `entry:${lprEntryId}:${reason}`
      : plate
        ? `plate:${plate}:${locationId}:${reason}`
        : '';
    if (dedupeKey) {
      const already = existing.find(alert => {
        const key = alert.lpr_entry_id
          ? `entry:${alert.lpr_entry_id}:${alert.reason}`
          : alert.plate
            ? `plate:${alert.plate}:${alert.location_id ?? ''}:${alert.reason}`
            : '';
        return key === dedupeKey;
      });
      if (already) {
        return;
      }
    }

    const id = `lane-attention-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const now = Date.now();
    const alert: LaneAttentionAlert = {
      id,
      reason,
      title,
      message,
      source_event: data['source_event'] != null ? String(data['source_event']) : null,
      location_id: locationId || null,
      plate: plate || null,
      state: data['state'] != null ? String(data['state']) : null,
      branch: data['branch'] != null ? String(data['branch']) : null,
      lane_id: data['lane_id'] != null ? String(data['lane_id']) : null,
      lane_name: data['lane_name'] != null ? String(data['lane_name']) : null,
      lpr_entry_id: lprEntryId || null,
      payload: isMultiEligible ? { ...data } : null,
      timestamp: new Date(),
      expiresAt: now + AUTO_DISMISS_MS,
    };

    const next = [alert, ...existing].slice(0, MAX_ALERTS);
    // Clear timers for alerts evicted by the cap so dropped entries are fully cleaned up.
    for (const dropped of existing) {
      if (!next.some(a => a.id === dropped.id)) {
        this.clearTimer(dropped.id);
      }
    }
    this.alertsSubject.next(next);
    this.scheduleAutoDismiss(id);
  }

  dismiss(id: string): void {
    this.clearTimer(id);
    this.alertsSubject.next(this.alertsSubject.value.filter(alert => alert.id !== id));
  }

  /**
   * CWS-1246: drop stacked alerts for an LPR entry once the attendant finishes (or closes)
   * the multi-eligible / rewash chooser so the "Active Subscription" toast does not linger.
   * CWS-1730: pass `reason` to drop only that kind (the refusal toast once its popup opens).
   */
  dismissForLprEntry(lprEntryId: string | null | undefined, reason?: string): void {
    const entryId = String(lprEntryId ?? '').trim();
    if (!entryId) {
      return;
    }

    const remaining: LaneAttentionAlert[] = [];
    let changed = false;
    for (const alert of this.alertsSubject.value) {
      if (alert.lpr_entry_id === entryId && (!reason || alert.reason === reason)) {
        changed = true;
        this.clearTimer(alert.id);
        continue;
      }
      remaining.push(alert);
    }
    if (changed) {
      this.alertsSubject.next(remaining);
    }
  }

  clearAll(): void {
    for (const id of this.dismissTimers.keys()) {
      this.clearTimer(id);
    }
    this.alertsSubject.next([]);
  }

  private scheduleAutoDismiss(id: string): void {
    this.clearTimer(id);
    const timer = setTimeout(() => {
      this.dismiss(id);
    }, AUTO_DISMISS_MS);
    this.dismissTimers.set(id, timer);
  }

  private clearTimer(id: string): void {
    const timer = this.dismissTimers.get(id);
    if (timer) {
      clearTimeout(timer);
      this.dismissTimers.delete(id);
    }
  }
}
